import { AwsUi } from '../../../core/runtime/src';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
import { Spinner } from '../../spinner/src';
import { Controller, ComponentFeature, getComponentSetter, updateController, checkReadinessAndInitializeController, SMALL_BREAKPOINT } from '../../table/src';
import utilities from './componentsUtilities';
import { getCardsPerRow } from './cardsLayoutHelper';
import CardsStickyHandler from './cards-sticky-handler';
import SelectionControl from '../../table/src/selection/selection-control';
var lastGeneratedName = 0;
var nextGeneratedName = function () { return "awsui-cards-" + lastGeneratedName++; };
export var Cards = AwsUi.__addComponent('awsui-cards', {
    releaseStatus: 'stable',
    events: {
        // Fired when the list of items that are visible in the cards changes upon user interaction.
        // Note that the initial render or a programmatic items change will not trigger the event.
        // If you need to cover these cases too, consider filtering and paginating items manually on your side.
        // The event `detail` contains the list of the visible items.
        visibleItemsChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'Cards.VisibleItemsChangeDetail'
        },
        // Fired when user decided to change visible content in the cards.
        // Event detail contain an array of field ids that the user decided to include in the view.
        // This property comes form the content-selection component.
        contentSelectionChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'Cards.ContentSelectionChangeDetail'
        },
        // Fired when updating cards properties and before the sticky header state is reset.
        // Canceling this event will prevent cards component from scrolling to then top and resetting sticky header state.
        beforeUnstick: {
            bubbles: true,
            cancelable: true
        }
    },
    elementQueries: [
        {
            read: {
                selector: '.awsui-cards-inner',
                properties: ['width']
            },
            apply: {
                __innerContentClass: function (_a) {
                    var width = _a.width;
                    var availableWidth = parseInt(width, 10);
                    var cardsPerRow = getCardsPerRow(availableWidth, this.cardsPerRow);
                    var isSmall = availableWidth <= SMALL_BREAKPOINT;
                    this.__stickyHandler.handleContainerChange();
                    return "grid-" + cardsPerRow + (isSmall ? ' awsui-cards-inner-small' : '');
                }
            }
        }
    ],
    regions: {
        // Use this region to add filtering, pagination, selection, sorting and preferences.
        tools: {
            isDefault: true
        },
        // Displayed on top of the tools region. Use it for the heading and the action stripe.
        header: {},
        // Displayed only when the list of items is empty.
        empty: {},
        // Displayed only when all available items are filtered out by the filtering component.
        noMatch: {}
    },
    generics: {
        Item: {
            defaultType: 'Table.Item'
        }
    },
    properties: {
        // This property defines what to display in each card. It has the following properties:
        // * `header` [Renderable]: responsible for the display of the card header
        // * `sections` [array]: responsible for the display of the card content. Cards can have many sections in their
        //   body. Each entry in the array is responsible for the display of a section. It has the following properties:
        //   * `id`: [string] identifier for that section.
        //     The `id` property is needed only if `awsui-cards-content-selector` component is used
        //     within `awsui-cards-preferences` component.
        //   * `header`: [Renderable]: responsible for the display of the header of a section
        //   * `content`: [Renderable]: responsible for the display of the content of a section.
        //   * `width`: [number] the width of the card section in percents. Use this to display multiple sections in
        //   the same row. By default the value is 100%
        //
        // All of the above properties are optional.
        //
        // **Renderable value**
        //   * **AngularJS:** Must be a string representing an Angular template. The current item is assigned to `$item` scope
        //     variable. **Security note:** the return value will be treated as HTML, make sure you have escaped the user
        //     input using [ngBind](https://docs.angularjs.org/api/ng/directive/ngBind) or [interpolation](https://docs.angularjs.org/guide/interpolation).
        //     Example: `'<a>{{$item.prop}}</a>'`.
        //   * **React:** Must be a React element or a function that receives the item as an argument and returns the React element
        //     to display.
        //     Example: `item => {return (<a>item.prop</a>);}` or `<span>static value</span>` in jsx.
        //   * **GWT:** Must be a lambda function that should return either a string, html element or GWT-widget instance.
        //     Example: `() -> 'some text'` or `() -> new HTMLPanel()`.
        cardDefinition: {
            type: 'object',
            objectType: 'Cards.CardDefinition',
            onChange: updateController('cardDefinition')
        },
        // The items serve as data source for a card.
        // The display of a card is handled by the `cardDefinition` property.
        //
        // _Remark:_ if you want to change the set of items make sure you provide a new array.
        // Any modification to the same array will not be reflected in the view.
        items: {
            type: 'array',
            itemTypes: ['Item'],
            onChange: updateController('items')
        },
        // Renders the cards as being in a loading state.
        // It is highly recommended to also set a `loadingText`.
        loading: {
            type: 'boolean'
        },
        // Text to be displayed when in loading state.
        loadingText: {
            type: 'string'
        },
        // List of the sub-components that will be present in the cards default region.
        // The list can contain `filtering`, `propertyFiltering`, `pagination`, `selection`.
        // Example: `['pagination', 'filtering', 'selection']`.
        //
        // **GWT, React 16:** For performance reasons, **this property is mandatory
        // if you are using GWT or React 16**. In Angular, you can leave it undefined.
        features: {
            type: 'array',
            itemTypes: ['string'],
            defaultValue: []
        },
        // This property allows you to specify the number of cards per row for any interval of container width.
        // It is an array whose entries are objects containing:
        // - `cards`: number: number of cards per row,
        // - `minWidth`: integer: it represents the minimum container width (in pixels) starting from which
        // the specified number of cards per row to be displayed.
        // The maximum container width of application is the lowest `minWidth` found in the array that is greater than this
        // one. The default value is `0`.
        //
        // For example, if set to
        // ```
        // [{
        //   cards: 1
        // }, {
        //   minWidth: 500,
        //   cards: 2
        // }]
        // ```
        //
        // then the cards component will display:
        // * 1 card per row if `0 <= containerWidth < 500`
        // * 2 cards per row if `containerWidth >= 500`.
        //
        // The number of cards per row cannot be greater than 20.
        //
        // Default value:
        // ```
        // [{
        //   cards: 1
        // }, {
        //   minWidth: 768,
        //   cards: 2
        // }, {
        //   minWidth: 992,
        //   cards: 3
        // }, {
        //   minWidth: 1200,
        //   cards: 4
        // }, {
        //   minWidth: 1400,
        //   cards: 5
        // }, {
        //   minWidth: 1920,
        //   cards: 6
        // }]
        // ```
        cardsPerRow: {
            type: 'array',
            itemTypes: ['Cards.CardsLayout'],
            onChange: function () {
                if (this.__componentState === 'attached') {
                    AwsUi.elementQueries.notifyChange();
                }
            }
        },
        // Set this to true if you want the cards header to remain visible when the user scrolls down
        stickyHeader: {
            type: 'boolean',
            onChange: function (oldValue, newValue) {
                this.__updateStickySetting(newValue);
            }
        },
        // Optionally provide a vertical offset (in pixels) for the sticky header,
        // for example if you need to position the sticky header below other fixed position elements on the page.
        stickyHeaderVerticalOffset: {
            type: 'integer',
            onChange: function (oldValue, newValue) {
                this.__updateStickyVerticalOffset(newValue);
            }
        },
        // calculated className based on available space
        __innerContentClass: {
            type: 'string'
        },
        // pagination component
        __pagination: {
            type: 'object',
            onChange: getComponentSetter(ComponentFeature.PAGINATION)
        },
        // filtering component
        __filtering: {
            type: 'object',
            onChange: getComponentSetter(ComponentFeature.FILTERING)
        },
        // property filtering component
        __propertyFiltering: {
            type: 'object',
            onChange: getComponentSetter(ComponentFeature.PROPERTY_FILTERING)
        },
        // selection component
        __selection: {
            type: 'object',
            onChange: getComponentSetter(ComponentFeature.SELECTION)
        },
        // preferences component
        __preferences: {
            type: 'object',
            onChange: function () {
                this.__updateToolsVisibility();
            }
        },
        // currenly visible content
        __contentSelection: {
            type: 'object'
        }
    },
    callbacks: {
        initialized: function () {
            this.__cardDefinitionReady = false;
            this.__itemsReady = false;
            this.__initializedFeatures = [];
            this.__currentCardsPerRow = null;
            this.__preferencesSettings = {};
            this.__controller = new Controller(this, utilities);
            this.__hideTools = false;
            this.__name = nextGeneratedName();
            this.__stickyHandler = new CardsStickyHandler(this.node, this.stickyHeaderVerticalOffset);
            this.__shiftPressed = false;
        },
        attached: function () {
            this.__checkReadinessAndInitializeController();
            if (this.stickyHeader) {
                this.__stickyHandler.attach();
            }
        },
        detached: function () {
            if (this.stickyHeader) {
                this.__stickyHandler.detach();
            }
        }
    },
    functions: {
        __getData: function () {
            return {
                items: this.items
            };
        },
        __checkReadinessAndInitializeController: checkReadinessAndInitializeController(['cardDefinition', 'items']),
        __getModifiedSections: function () {
            if (!this.cardDefinition || !this.cardDefinition.sections) {
                return [];
            }
            var contentSelection = this.__contentSelection;
            var sections = this.cardDefinition.sections;
            if (!contentSelection) {
                return sections;
            }
            var modifiedSections = [];
            contentSelection.forEach(function (id) {
                sections.some(function (sectionDef) {
                    if (sectionDef.id === id) {
                        modifiedSections.push(sectionDef);
                        return true;
                    }
                });
            });
            return modifiedSections;
        },
        __updateToolsVisibility: function () {
            this.__hideTools = !(this.__preferences || this.__filtering || this.__propertyFiltering || this.__pagination);
        },
        __updateSelectionLabels: function () {
            // Selection labels are in every card, so we need to re-render everything
            this.__update();
        },
        __fireContentSelectionChangeEvent: function () {
            var eventDetails = { contentSelection: this.__contentSelection };
            this.__fireEvent('contentSelectionChange', eventDetails);
        },
        __fireVisibleItemsChangeEvent: function () {
            var visibleItemsChangeDetail = {
                visibleItems: this.__controller.getVisibleItems()
            };
            this.__fireEvent('visibleItemsChange', visibleItemsChangeDetail);
        },
        __updateStickySetting: function (enabled) {
            if (this.__componentState === 'attached') {
                if (enabled) {
                    this.__stickyHandler.attach();
                }
                else {
                    this.__stickyHandler.detach();
                }
            }
        },
        __updateStickyVerticalOffset: function (value) {
            if (this.__stickyHandler) {
                this.__stickyHandler.setVerticalOffset(value);
            }
        }
    },
    render: function (m, h) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        var component = this;
        var cardDefinition = component.cardDefinition;
        var sections = this.__getModifiedSections();
        var controller = this.__controller;
        var visibleItems = controller.getVisibleItems();
        var isSelectionEnabled = !!component.__selection;
        function toMithril(lifecycleKey, renderer, item) {
            return component.renderToMithril(lifecycleKey, renderer, [{ $item: item }]);
        }
        var selectionAriaLabels = function () {
            var labels = component.__selection.__getSelectionLabels();
            var selectionData = component.__selection.__getSelectionState();
            var cb = new LegacyClassBuilder(component)
                .addComponentClass('selection-aria-labels')
                .addClass('awsui-util-hide');
            return (AwsUi.__createElement("span", { className: cb.toClassName(), "aria-hidden": "true" },
                AwsUi.__createElement("span", { id: component.__name + "-selection-group" }, labels.selectionGroupLabel),
                AwsUi.__createElement("span", { id: component.__name + "-selection-all" }, labels.allItemsSelectionLabel(selectionData)),
                visibleItems.map(function (item, index) { return (AwsUi.__createElement("span", { key: component.__name + "-selection-" + index, id: component.__name + "-selection-" + index }, labels.itemSelectionLabel(selectionData, item))); })));
        };
        function cardDefinitionHeader(item, index) {
            if (cardDefinition.header) {
                return toMithril("card-header-" + index, cardDefinition.header, item);
            }
        }
        function cardHeader(item, index) {
            return (AwsUi.__createElement("div", { className: "awsui-cards-card-header" },
                AwsUi.__createElement("span", { className: "awsui-cards-card-header-inner", id: component.__name + "-" + index + "-header" }, cardDefinitionHeader(item, index)),
                isSelectionEnabled ? (AwsUi.__createElement(SelectionControl, { name: component.__name, wrapper: "span", baseClassName: "awsui-cards-card", itemId: "" + index, selectionType: component.__selection.selectionType, parentComponent: component, checked: controller.isItemSelected(item), disabled: controller.isItemDisabled(item), onShiftToggle: function (shiftPressed) { return (component.__shiftPressed = shiftPressed); }, onClick: function () { return controller.toggleItem(item, index, component.__shiftPressed); }, onFocusUp: function () { return controller.moveFocus(index, -1); }, onFocusDown: function () { return controller.moveFocus(index, 1); } })) : ('')));
        }
        function cardSections(item, itemIndex) {
            return sections.map(function (sectionDefinition, sectionIndex) {
                return cardSection(sectionDefinition, item, itemIndex, sectionIndex);
            });
        }
        function cardSection(sectionDefinition, item, itemIndex, sectionIndex) {
            var header = sectionDefinition.header, content = sectionDefinition.content;
            var headerOutput = header ? toMithril("cards-header-" + itemIndex + "-" + sectionIndex, header, item) : '';
            var contentOutput = content ? toMithril("cards-content-" + itemIndex + "-" + sectionIndex, content, item) : '';
            var width = sectionDefinition.width || 100;
            return (AwsUi.__createElement("div", { className: "awsui-cards-card-section", style: { width: width + "%" } },
                headerOutput ? AwsUi.__createElement("div", { className: "awsui-cards-card-section-header" }, headerOutput) : '',
                contentOutput ? AwsUi.__createElement("div", { className: "awsui-cards-card-section-content" }, contentOutput) : ''));
        }
        function card(item, index) {
            var cbCard = new LegacyClassBuilder(component)
                .addComponentClass('card-container')
                .addComponentClass('card-selectable', isSelectionEnabled)
                .addComponentClass('card-selected', controller.isItemSelected(item));
            return (AwsUi.__createElement("li", { className: cbCard.toClassName(), "aria-labelledby": component.__name + "-" + index + "-header" },
                AwsUi.__createElement("div", { className: "awsui-cards-card-container-inner" },
                    cardHeader(item, index),
                    cardSections(item, index))));
        }
        function body() {
            var cbContainer = new LegacyClassBuilder(component).addComponentClass('container');
            var cbLoading = new LegacyClassBuilder(component).addComponentClass('loading');
            if (component.loading) {
                return (AwsUi.__createElement("div", { className: cbContainer.toClassName() + " " + cbLoading.toClassName() },
                    AwsUi.__createElement("span", { className: "awsui-util-status-inactive" },
                        AwsUi.__createElement(Spinner, null),
                        " ",
                        component.loadingText)));
            }
            if (!component.__itemsReady) {
                return AwsUi.__createElement("div", { className: "awsui-cards-empty" });
            }
            if (component.items.length === 0) {
                return AwsUi.__createElement("div", { className: "awsui-cards-empty" }, h.region('empty'));
            }
            if (visibleItems.length === 0) {
                return AwsUi.__createElement("div", { className: "awsui-cards-empty" }, h.region('noMatch'));
            }
            return (AwsUi.__createElement("ol", { className: cbContainer.toClassName(), lifecycleKey: "cards-list", componentDidMount: component.__stickyHandler.addItemsFocusListener, componentWillUnmount: component.__stickyHandler.removeItemsFocusListener }, visibleItems.map(function (item, index) { return card(item, index); })));
        }
        var toolsCb = new LegacyClassBuilder(component)
            .addComponentClass('tools')
            .addComponentClass('tools-hidden', component.__hideTools)
            .addComponentClass('has-pagination', component.__pagination)
            .addComponentClass('has-property-filter', component.__propertyFiltering);
        var isRegionsContainerEmpty = !this.header && (!this.tools || component.__hideTools);
        var regionsContainerCb = new LegacyClassBuilder(component)
            .addComponentClass('regions-container')
            .addComponentClass('regions-container-empty', isRegionsContainerEmpty);
        var innerContainerCb = new LegacyClassBuilder(component)
            .addComponentClass('inner')
            .addClass(this.__innerContentClass)
            .addClass(this.__stickyHandler.getCurrentStickyClass());
        return (AwsUi.__createElement("div", { className: innerContainerCb.toClassName() },
            AwsUi.__createElement("div", { className: "awsui-cards-heading-strut" }),
            AwsUi.__createElement("div", { className: regionsContainerCb.toClassName() },
                h.region('header', 'div', { className: 'awsui-cards-header' }),
                h.region('tools', 'div', { className: toolsCb.toClassName() }),
                isSelectionEnabled ? selectionAriaLabels() : null),
            body()));
    }
});
//# sourceMappingURL=index.js.map